import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { stateful, useEffect, useState } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { html, nothing } from 'lit';

export const PasswordResetPage = stateful(() => {
  const [token, setToken] = useState<string>('');
  const [firstPassword, setFirstPassword] = useState<string>('');
  const [repeatedPassword, setRepeatedPassword] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    const params = new URL(window.location.toString()).searchParams;
    setToken(params.get('token') ?? '');
  }, []);

  const _changePassword = async () => {
    setErrorMessage('');

    if (!token) {
      setErrorMessage('Invalid or missing reset token.');
      return;
    }
    if (firstPassword !== repeatedPassword) {
      setErrorMessage('Passwords do not match.');
      return;
    }
    if (!firstPassword) {
      setErrorMessage('Please enter a password.');
      return;
    }

    setLoading(true);
    try {
      const result = await AdornisUser.changePasswordWithToken(token, firstPassword)();
      if (result) {
        setSuccess(true);
      }
    } catch (e: unknown) {
      const msg =
        typeof e === 'object' && e !== null && 'message' in e ? String(e.message) : 'Failed to change password.';
      setErrorMessage(msg);
    } finally {
      setLoading(false);
    }
  };

  const _form = () => html`
    <div class="flex flex-col gap-4 w-full">
      <p class="text-base text-base-content/70 text-center">
        Enter a new password below. Make sure it meets the required strength.
      </p>
      <label class="form-control w-full">
        <div class="label"><span class="label-text">New Password</span></div>
        ${TextInput({
          type: 'password',
          placeholder: 'New password',
          value: firstPassword,
          onChanged: v => setFirstPassword(v ?? ''),
          onSubmit: _changePassword,
        })}
      </label>
      <label class="form-control w-full">
        <div class="label"><span class="label-text">Repeat Password</span></div>
        ${TextInput({
          type: 'password',
          placeholder: 'Repeat password',
          value: repeatedPassword,
          onChanged: v => setRepeatedPassword(v ?? ''),
          onSubmit: _changePassword,
        })}
      </label>
      ${errorMessage ? html`<div class="alert alert-error alert-sm">${errorMessage}</div>` : nothing}
      <button class="btn btn-primary w-full" ?disabled=${loading} @click=${_changePassword}>
        ${loading ? html`<span class="loading loading-spinner loading-sm"></span>` : 'Change Password'}
      </button>
    </div>
  `;

  const _success = () => html`
    <div class="flex flex-col gap-4 items-center w-full">
      <div class="alert alert-success alert-soft">Your password has been changed successfully.</div>
      <button class="btn btn-primary w-full" @click=${() => goTo('/')}>Go to Login</button>
    </div>
  `;

  const _noToken = () => html`
    <div class="alert alert-error alert-soft text-center">Invalid or missing reset token.</div>
  `;

  return html`
    <div
      class="min-h-screen flex items-center justify-center p-4 bg-linear-to-br from-base-100 via-primary/5 to-base-200"
    >
      <div class="card w-full max-w-md bg-base-100 shadow-xl border border-primary/20">
        <div class="card-body flex flex-col gap-4">
          <h1 class="text-2xl font-bold text-center">Change Password</h1>
          ${!token ? _noToken() : success ? _success() : _form()}
        </div>
      </div>
    </div>
  `;
});
